import axios from 'axios'

// Railway 배포 URL (프로덕션)
const PRODUCTION_API_URL = 'https://cams-mold-management-system-production-b7d0.up.railway.app/api/v1'
// VITE_API_URL이 이미 /api/v1을 포함하면 그대로 사용, 아니면 /api/v1 추가
const API_URL = import.meta.env.VITE_API_URL || PRODUCTION_API_URL || 'http://localhost:3001/api/v1'

// 백엔드 기본 URL (이미지 등 정적 파일용) - 항상 프로덕션 백엔드 URL 사용
const BACKEND_BASE_URL = 'https://cams-mold-management-system-production-b7d0.up.railway.app'
const API_BASE_URL = API_URL.replace('/api/v1', '')

/**
 * 이미지 URL을 전체 URL로 변환
 * - http/https로 시작하면 그대로 반환
 * - /api/v1/mold-images/file/로 시작하면 백엔드 URL 붙임
 * - /uploads/로 시작하면 백엔드 URL 붙임
 * - data:로 시작하면 (Base64) 그대로 반환
 */
export const getImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return null;

  // 이미 전체 URL이면 그대로 반환
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // Base64 데이터면 그대로 반환
  if (imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  // 잘못된 URL 패턴 필터링 (과거 업로드 버그로 저장된 데이터)
  if (imageUrl.includes('/undefined') || imageUrl.includes('/null')) {
    return null;
  }

  // 상대 경로 (/api/... 또는 /uploads/...)만 백엔드 URL 붙임
  // 그 외 문자열(부품명 등 잘못된 값)은 무시
  if (imageUrl.startsWith('/')) {
    return `${BACKEND_BASE_URL}${imageUrl}`;
  }

  // 유효하지 않은 URL 패턴이면 null 반환
  return null;
}

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // httpOnly 쿠키 자동 전송
  timeout: 30000, // 30초 글로벌 타임아웃 — 무한 로딩 방지
  headers: {
    'Content-Type': 'application/json',
  },
})

// === 토큰 메모리 관리 (cross-origin 쿠키 차단 fallback) ===
// Phase JZ-15 H-1: sessionStorage 동기화 — 모바일 PWA 새로고침 시 memoryToken 휘발 → 재로그인 강제 차단
//   탭 단위 생존 (sessionStorage), 보안: 탭 닫으면 자동 삭제, XSS 회피는 httpOnly 쿠키와 동등
const SESSION_TOKEN_KEY = 'cams-session-token'
let memoryToken = (() => {
  try { return sessionStorage.getItem(SESSION_TOKEN_KEY) || null } catch (e) { return null }
})()
export const setMemoryToken = (token) => {
  memoryToken = token
  try {
    if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token)
    else sessionStorage.removeItem(SESSION_TOKEN_KEY)
  } catch (e) { /* Safari Private 등 — 메모리만 사용 */ }
}
export const getMemoryToken = () => memoryToken
export const clearMemoryToken = () => {
  memoryToken = null
  try { sessionStorage.removeItem(SESSION_TOKEN_KEY) } catch (e) { /* noop */ }
}

// Request interceptor: 메모리 토큰이 있으면 Authorization 헤더 추가
api.interceptors.request.use((config) => {
  if (memoryToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${memoryToken}`
  }
  // 정책: GPS 자동 첨부 제거 (2026-04-17) — QR 스캔 흐름에서만 명시적 전송
  return config
})

/**
 * API 에러 메시지 추출 헬퍼
 * @param {Error} error - axios error 객체
 * @param {string} fallback - 기본 에러 메시지
 * @returns {string}
 */
export const formatApiError = (error, fallback = '요청 처리에 실패했습니다.') => {
  const errData = error?.response?.data?.error
  if (errData?.details) return `${errData.message}: ${errData.details}`
  if (errData?.message) return errData.message
  if (error?.response?.data?.message) return error.response.data.message
  if (error?.message) return error.message
  return fallback
}

// === 토큰 리프레시 + 401 인터셉터 ===
let isRefreshing = false
let failedQueue = [] // 리프레시 대기 중인 요청 큐
let isRedirecting = false
let authRestoredUntil = 0

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve()
  })
  failedQueue = []
}

// 재로그인 성공 시 일정 시간 401 무시 (데이터 재로드 중 이전 요청의 401 방지)
window.addEventListener('cams:auth-restored', () => {
  authRestoredUntil = Date.now() + 3000
})

// 세션 만료 → 로그인 페이지로 이동 (리프레시 실패 시)
const forceLogout = () => {
  if (isRedirecting) return
  isRedirecting = true
  clearMemoryToken()
  localStorage.removeItem('cams-auth')

  const currentPath = window.location.pathname
  const isMobile = currentPath.startsWith('/mobile') || currentPath.startsWith('/m/')

  if (isMobile) {
    window.dispatchEvent(new CustomEvent('cams:auth-expired', {
      detail: { path: currentPath }
    }))
    setTimeout(() => { isRedirecting = false }, 2000)
  } else {
    setTimeout(() => {
      window.location.replace('/login')
      isRedirecting = false
    }, 100)
  }
}

// Response interceptor: 401 → 자동 토큰 리프레시 → 재요청
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status !== 401) return Promise.reject(error)

    // 인증 관련 API는 리프레시 시도 안 함
    const requestUrl = originalRequest?.url || ''
    const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh')
    if (isAuthRequest) return Promise.reject(error)

    // 재로그인 직후 유예 구간
    if (Date.now() < authRestoredUntil) return Promise.reject(error)

    // 이미 리프레시 시도한 요청이면 강제 로그아웃
    if (originalRequest._retry) {
      forceLogout()
      return Promise.reject(error)
    }

    // TOKEN_EXPIRED일 때만 리프레시 시도 (변조 등은 바로 로그아웃)
    const errorCode = error.response?.data?.error?.code
    const errorMsg = error.response?.data?.error?.message || ''
    const isExpired = errorCode === 'TOKEN_EXPIRED' || errorMsg.includes('expired') || errorMsg === 'Token expired'

    if (!isExpired) {
      forceLogout()
      return Promise.reject(error)
    }

    // 리프레시 진행 중이면 큐에 대기
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then(() => {
        originalRequest._retry = true
        return api(originalRequest) // 쿠키가 자동으로 갱신된 토큰 전송
      }).catch((err) => Promise.reject(err))
    }

    // 리프레시 실행 (withCredentials로 쿠키 자동 전송 → 새 쿠키 수신)
    isRefreshing = true
    originalRequest._retry = true

    try {
      const refreshRes = await api.post('/auth/refresh')
      // 서버가 body에 토큰도 반환 → 메모리 토큰 갱신 (쿠키 차단 시 fallback)
      const newToken = refreshRes.data?.data?.token
      if (newToken) setMemoryToken(newToken)

      // 대기 중인 요청 모두 재실행
      processQueue(null)

      // 원래 요청 재시도 (쿠키 또는 Authorization 헤더 자동 사용)
      return api(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      forceLogout()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  // 모바일 QR 로그인 및 스캔 엔드포인트는 서버의 /mobile/qr 라우트에 맞춤
  qrLogin: (data) => api.post('/mobile/qr/login', data),
  logout: () => api.post('/auth/logout'),
  refreshToken: (token) => api.post('/auth/refresh', { token }),
  me: () => api.get('/auth/me'),
  scanQR: (data) => api.post('/mobile/qr/scan', data),
}

// Mold API
export const moldAPI = {
  getAll: (params) => api.get('/molds', { params }),
  getById: (id) => api.get(`/molds/${id}`),
  getByQR: (qrCode) => api.get(`/molds/qr/${qrCode}`),
  create: (data) => api.post('/molds', data),
  update: (id, data) => api.patch(`/molds/${id}`, data),
  getHistory: (id) => api.get(`/molds/${id}/history`),
  getLocations: () => api.get('/hq/mold-locations'), // 금형 위치 조회
}

// 운영 게시판 (공지사항 / Q&A / 건의·개선요청) — 2026-06-30
export const boardAPI = {
  list: (params) => api.get('/board', { params }),
  getById: (id) => api.get(`/board/${id}`),
  create: (data) => api.post('/board', data),
  update: (id, data) => api.put(`/board/${id}`, data),
  remove: (id) => api.delete(`/board/${id}`),
  addComment: (id, data) => api.post(`/board/${id}/comments`, data),
  updateComment: (commentId, data) => api.put(`/board/comments/${commentId}`, data),
  removeComment: (commentId) => api.delete(`/board/comments/${commentId}`),
  uploadPhoto: (formData) => api.post('/board/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getReaders: (id) => api.get(`/board/${id}/readers`),   // 읽음 사용자 현황 (관리자 전용)
}

// 대여자산 실사 (SP-1) — PC · Mobile 공유
export const assetAuditAPI = {
  // 캠페인
  listCampaigns: (params) => api.get('/asset-audits/campaigns', { params }),
  getCampaign: (id) => api.get(`/asset-audits/campaigns/${id}`),
  createCampaign: (data) => api.post('/asset-audits/campaigns', data),
  updateCampaign: (id, data) => api.patch(`/asset-audits/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/asset-audits/campaigns/${id}`),
  getSummary: (id, params) => api.get(`/asset-audits/campaigns/${id}/summary`, { params }),
  // 그룹
  listGroups: (id) => api.get(`/asset-audits/campaigns/${id}/groups`),
  createGroup: (id, data) => api.post(`/asset-audits/campaigns/${id}/groups`, data),
  updateGroup: (groupId, data) => api.patch(`/asset-audits/groups/${groupId}`, data),
  deleteGroup: (groupId) => api.delete(`/asset-audits/groups/${groupId}`),
  addMembers: (groupId, userIds) => api.post(`/asset-audits/groups/${groupId}/members`, { user_ids: userIds }),
  removeMember: (groupId, userId) => api.delete(`/asset-audits/groups/${groupId}/members/${userId}`),
  copyGroups: (id, sourceCampaignId) => api.post(`/asset-audits/campaigns/${id}/groups/copy-from/${sourceCampaignId}`),
  // 할당
  listTargets: (params) => api.get('/asset-audits/assignments/targets', { params }),
  listAssignments: (id, params) => api.get(`/asset-audits/campaigns/${id}/assignments`, { params }),
  bulkAssign: (id, data) => api.post(`/asset-audits/campaigns/${id}/assignments/bulk`, data),
  updateAssignment: (id, data) => api.patch(`/asset-audits/assignments/${id}`, data),
  deleteAssignment: (id) => api.delete(`/asset-audits/assignments/${id}`),
  getAssignment: (id) => api.get(`/asset-audits/assignments/${id}`),
  // 결과
  submitResult: (id, data) => api.post(`/asset-audits/assignments/${id}/results`, data),
  listResults: (params) => api.get('/asset-audits/results', { params }),
  // 협력사 self-report
  listMyAssignments: (params) => api.get('/asset-audits/my-assignments', { params }),
  reportMyAssignment: (id, data) => api.post(`/asset-audits/my-assignments/${id}/report`, data),
}

// Checklist API
export const checklistAPI = {
  startDaily: (data) => api.post('/checklists/daily/start', data),
  updateDaily: (id, data) => api.patch(`/checklists/daily/${id}`, data),
  getDaily: (id) => api.get(`/checklists/daily/${id}`),
  getHistory: (params) => api.get('/checklists/history', { params }),
  uploadPhotos: (formData) => api.post('/checklists/photos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
}

// Inspection API (점검 승인 관리)
export const inspectionAPI = {
  // 점검 목록 조회
  getAll: (params) => api.get('/inspections', { params }),
  // 승인 대기 목록
  getPending: (params) => api.get('/inspections/pending', { params }),
  // 점검 상세 조회
  getById: (id, params) => api.get(`/inspections/${id}`, { params }),
  // 일상점검 제출
  createDaily: (data) => api.post('/inspections/daily', data),
  // 정기점검 제출
  createPeriodic: (data) => api.post('/inspections/periodic', data),
  // 점검 수정
  update: (id, data) => api.patch(`/inspections/${id}`, data),
  // 점검 승인
  approve: (id, data) => api.post(`/inspections/${id}/approve`, data),
  // 점검 반려
  reject: (id, data) => api.post(`/inspections/${id}/reject`, data),
}

// Periodic Inspection API (정기점검 관리)
export const periodicInspectionAPI = {
  // 정기점검 목록 조회
  getAll: (params) => api.get('/periodic-inspections', { params }),
  // 정기점검 상세 조회
  getById: (id) => api.get(`/periodic-inspections/${id}`),
  // 정기점검 생성
  create: (data) => api.post('/periodic-inspections', data),
  // 정기점검 수정
  update: (id, data) => api.put(`/periodic-inspections/${id}`, data),
  // 다음 정기점검 정보 조회
  getNextInspection: (moldId) => api.get(`/periodic-inspections/mold/${moldId}/next`),
  // 점검 유형별 체크리스트 항목 조회 (세척 항목 포함)
  getChecklistItems: (inspectionType) => api.get(`/periodic-inspections/checklist-items/${inspectionType}`),
  // 사진 업로드
  uploadPhotos: (inspectionId, formData) => api.post(`/periodic-inspections/${inspectionId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // 사진 목록 조회
  getPhotos: (inspectionId, params) => api.get(`/periodic-inspections/${inspectionId}/photos`, { params }),
  // 사진 삭제
  deletePhoto: (inspectionId, photoId) => api.delete(`/periodic-inspections/${inspectionId}/photos/${photoId}`),
}

// Transfer API
export const transferAPI = {
  getAll: (params) => api.get('/transfers', { params }),
  getById: (id) => api.get(`/transfers/${id}`),
  create: (data) => api.post('/transfers', data),
  update: (id, data) => api.patch(`/transfers/${id}`, data),
  approve: (id, data) => api.patch(`/transfers/${id}/approve`, data),
  reject: (id, data) => api.patch(`/transfers/${id}/reject`, data),
  getChecklistItems: () => api.get('/transfers/checklist/items'),
}

// Repair Request API (수리요청)
export const repairRequestAPI = {
  // 수리요청 목록 조회
  getAll: (params) => api.get('/repair-requests', { params }),
  // 수리요청 상세 조회
  getById: (id) => api.get(`/repair-requests/${id}`),
  // 수리요청 생성
  create: (data) => api.post('/repair-requests', data),
  // 수리요청 수정
  update: (id, data) => api.put(`/repair-requests/${id}`, data),
  // 수리요청 상태 변경
  updateStatus: (id, data) => api.patch(`/repair-requests/${id}/status`, data),
  // 수리요청 삭제
  delete: (id) => api.delete(`/repair-requests/${id}`),
  // 금형별 수리요청 조회
  getByMold: (moldId) => api.get('/repair-requests', { params: { mold_id: moldId } }),
  // 통계 조회
  getStats: (params) => api.get('/repair-requests/stats', { params }),
  // 알림 발송
  sendNotification: (data) => api.post('/workflow/notifications/send', data),
}

// Repair Step Workflow API (단계별 임시저장/승인요청/순차승인)
export const repairStepWorkflowAPI = {
  create: (data) => api.post('/repair-step-workflow', data),
  getById: (id) => api.get(`/repair-step-workflow/${id}`),
  getByMold: (moldSpecId) => api.get(`/repair-step-workflow/by-mold/${moldSpecId}`),
  saveDraft: (id, step, data) => api.put(`/repair-step-workflow/${id}/steps/${step}/draft`, data),
  submit: (id, step, data) => api.post(`/repair-step-workflow/${id}/steps/${step}/submit`, data),
  requestApproval: (id, step, data) => api.post(`/repair-step-workflow/${id}/steps/${step}/request-approval`, data),
  approve: (id, step, data) => api.post(`/repair-step-workflow/${id}/steps/${step}/approve`, data),
  reject: (id, step, data) => api.post(`/repair-step-workflow/${id}/steps/${step}/reject`, data),
  getHistory: (id) => api.get(`/repair-step-workflow/${id}/history`),
  getPendingApprovals: () => api.get('/repair-step-workflow/my/pending-approvals'),
  delete: (id) => api.delete(`/repair-step-workflow/${id}`),
  getListByMold: (moldSpecId, params) => api.get(`/repair-step-workflow/by-mold/${moldSpecId}`, { params }),
  getHistoryByMold: (moldSpecId) => api.get(`/repair-step-workflow/history-by-mold/${moldSpecId}`),
}

// Report API
export const reportAPI = {
  generate: (data) => api.post('/reports/generate', data),
  get: (id) => api.get(`/reports/${id}`),
  getTransfer: (transferId) => api.get(`/reports/transfer/${transferId}`),
  getInspection: (inspectionId) => api.get(`/reports/inspection/${inspectionId}`),
}

/** A/S 금형 대장 (SP-4) — 양산에서 내려왔지만 대여자산 실사 대상인 금형 */
export const asMoldAPI = {
  list: (params) => api.get('/as-molds', { params }),
  get: (id) => api.get(`/as-molds/${id}`),
  create: (data) => api.post('/as-molds', data),
  bulkCreate: (items) => api.post('/as-molds/bulk', { items }),
  update: (id, data) => api.patch(`/as-molds/${id}`, data),
  remove: (id) => api.delete(`/as-molds/${id}`),
};

// Mold Specification API
// Phase JZ-50: 지도 마커 ID 누출 차단 헬퍼
const isInvalidNumericId = (id) =>
  id == null || (typeof id === 'string' && /^(base-|current-)/.test(id));

export const moldSpecificationAPI = {
  create: (data) => api.post('/mold-specifications', data),
  getAll: (params) => api.get('/mold-specifications', { params }),
  // 2026-06-16: config 옵션 추가 — moldId fallback 호출처가 validateStatus 로 404 silent 처리 가능
  //   (moldId 가 molds.id 인 경우 spec 404 정상 폴백 — 콘솔 빨간 로그 방지)
  getById: (id, config) => {
    if (isInvalidNumericId(id)) {
      return Promise.resolve({ data: { success: false, error: { message: 'Invalid spec id (marker leak blocked)' } } });
    }
    return api.get(`/mold-specifications/${id}`, config);
  },
  update: (id, data) => api.patch(`/mold-specifications/${id}`, data),
  delete: (id) => api.delete(`/mold-specifications/${id}`),
  // 동일금형 복사 (capa | design_change) + 기존 폐기 선택
  copy: (id, data) => api.post(`/mold-specifications/${id}/copy`, data),
  checkDuplicate: (params) => api.get('/mold-specifications/check-duplicate', { params }),
}

// Mold Image API
// 다양한 연계 항목 지원: 금형정보, 체크리스트, 점검, 수리, 이관 등
export const moldImageAPI = {
  upload: (formData) => api.post('/mold-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // params: mold_id, mold_spec_id, image_type, reference_type, reference_id, checklist_id, repair_id, transfer_id, maker_spec_id
  getAll: (params) => api.get('/mold-images', { params }),
  // 특정 연계 항목의 이미지 조회
  getByReference: (referenceType, referenceId) => api.get('/mold-images', { 
    params: { reference_type: referenceType, reference_id: referenceId } 
  }),
  getByChecklist: (checklistId) => api.get('/mold-images', { params: { checklist_id: checklistId } }),
  getByRepair: (repairId) => api.get('/mold-images', { params: { repair_id: repairId } }),
  getByTransfer: (transferId) => api.get('/mold-images', { params: { transfer_id: transferId } }),
  setPrimary: (id) => api.patch(`/mold-images/${id}/primary`),
  delete: (id) => api.delete(`/mold-images/${id}`),
}

// Maker Specification API
export const makerSpecificationAPI = {
  getAll: (params) => api.get('/maker-specifications', { params }),
  getById: (id) => api.get(`/maker-specifications/${id}`),
  update: (id, data) => api.patch(`/maker-specifications/${id}`, data),
  getDashboardStats: () => api.get('/maker-specifications/dashboard/stats'),
  // 대시보드 보조 API (제작처 위젯)
  getActiveRepairs: (limit = 5) => api.get('/maker/repair-requests', {
    params: { status: 'in_progress,assigned', limit },
  }),
  getPendingShipments: (limit = 5) => api.get('/repair-shipment-checklists', {
    params: { status: 'pending_approval', limit },
  }),
}

// Injection Condition API (사출조건 관리)
export const injectionConditionAPI = {
  // 사출조건 등록 (제작처/생산처)
  create: (data) => api.post('/injection-conditions', data),
  // 사출조건 임시저장
  saveDraft: (data) => api.post('/injection-conditions/draft', data),
  // 사출조건 임시저장 초기화 (draft 삭제) — 2026-06-19
  deleteDraft: (moldSpecId) => api.delete('/injection-conditions/draft', { params: { mold_spec_id: moldSpecId } }),
  // 사출조건 승인요청
  requestApproval: (data) => api.post('/injection-conditions/request-approval', data),
  // Phase HL 임시: 협력사 자체 완료 (본사 승인 불요)
  completeSelf: (data) => api.post('/injection-conditions/complete-self', data),
  // 사출조건 조회 (금형별)
  get: (params) => api.get('/injection-conditions', { params }),
  // 사출조건 수정
  update: (id, data) => api.put(`/injection-conditions/${id}`, data),
  // 사출조건 승인/반려 (개발담당자)
  approve: (id, data) => api.post(`/injection-conditions/${id}/approve`, data),
  // 이력 조회
  getHistory: (params) => api.get('/injection-conditions/history', { params }),
  // 통계 조회
  getStats: (params) => api.get('/injection-conditions/stats', { params }),
  // 승인 대기 목록
  getPending: () => api.get('/injection-conditions/pending'),
}

// Weight API (설계중량/실중량 이력관리)
export const weightAPI = {
  // 현재 중량 조회
  get: (moldSpecId) => api.get(`/weight/${moldSpecId}`),
  // 중량 업데이트 (이력 자동 기록)
  update: (moldSpecId, data) => api.put(`/weight/${moldSpecId}`, data),
  // 중량 이력 조회
  getHistory: (moldSpecId, params) => api.get(`/weight/${moldSpecId}/history`, { params }),
}

// Material API (원재료 정보 이력관리)
export const materialAPI = {
  // 현재 원재료 정보 조회
  get: (moldSpecId) => api.get(`/material/${moldSpecId}`),
  // 원재료 정보 업데이트 (이력 자동 기록, 개발담당자만)
  update: (moldSpecId, data) => api.put(`/material/${moldSpecId}`, data),
  // 원재료 이력 조회
  getHistory: (moldSpecId, params) => api.get(`/material/${moldSpecId}/history`, { params }),
}

// Master Data API
export const masterDataAPI = {
  // 차종
  getCarModels: (params) => api.get('/master-data/car-models', { params }),
  createCarModel: (data) => api.post('/master-data/car-models', data),
  updateCarModel: (id, data) => api.patch(`/master-data/car-models/${id}`, data),
  deleteCarModel: (id) => api.delete(`/master-data/car-models/${id}`),
  
  // 재질
  getMaterials: (params) => api.get('/master-data/materials', { params }),
  createMaterial: (data) => api.post('/master-data/materials', data),
  updateMaterial: (id, data) => api.patch(`/master-data/materials/${id}`, data),
  deleteMaterial: (id) => api.delete(`/master-data/materials/${id}`),
  
  // 금형타입
  getMoldTypes: (params) => api.get('/master-data/mold-types', { params }),
  createMoldType: (data) => api.post('/master-data/mold-types', data),
  updateMoldType: (id, data) => api.patch(`/master-data/mold-types/${id}`, data),
  deleteMoldType: (id) => api.delete(`/master-data/mold-types/${id}`),
  
  // 톤수 (사출기 사양)
  getTonnages: (params) => api.get('/master-data/tonnages', { params }),
  createTonnage: (data) => api.post('/master-data/tonnages', data),
  updateTonnage: (id, data) => api.patch(`/master-data/tonnages/${id}`, data),
  deleteTonnage: (id) => api.delete(`/master-data/tonnages/${id}`),
  // 금형사이즈/형체력 기반 사출기 추천
  recommendTonnages: (params) => api.get('/master-data/tonnages/recommend', { params }),
  // 제작처 사출기 자동 수집
  syncTonnagesFromMakers: () => api.post('/master-data/tonnages/sync-from-makers'),
  
  // 원재료
  getRawMaterials: (params) => api.get('/master-data/raw-materials', { params }),
  createRawMaterial: (data) => api.post('/master-data/raw-materials', data),
  updateRawMaterial: (id, data) => api.patch(`/master-data/raw-materials/${id}`, data),
  deleteRawMaterial: (id) => api.delete(`/master-data/raw-materials/${id}`),
  
  // 회사 (제작처/생산처)
  getCompanies: (params) => api.get('/companies', { params }),
  // 내 업체 프로필
  getMyProfile: () => api.get('/companies/my-profile'),
  updateMyProfile: (data) => api.patch('/companies/my-profile', data),
  // 전체 등록 사출기 목록 (기초정보에서 불러오기용)
  getAllInjectionMachines: () => api.get('/companies/injection-machines/all'),
}

// Equipment API (장비 마스터 + 업체별 보유장비 + 캐파 분석)
export const equipmentAPI = {
  // 장비 마스터 (기초정보)
  getMasters: (params) => api.get('/equipment/masters', { params }),
  getMasterById: (id) => api.get(`/equipment/masters/${id}`),
  createMaster: (data) => api.post('/equipment/masters', data),
  updateMaster: (id, data) => api.patch(`/equipment/masters/${id}`, data),
  deleteMaster: (id) => api.delete(`/equipment/masters/${id}`),
  getManufacturers: (params) => api.get('/equipment/masters/manufacturers', { params }),
  // 내 업체 보유장비
  getMyEquipments: (params) => api.get('/equipment/my', { params }),
  addMyEquipment: (data) => api.post('/equipment/my', data),
  bulkAddMyEquipments: (data) => api.post('/equipment/my/bulk', data),
  // 특정 업체 보유장비
  getCompanyEquipments: (companyId, params) => api.get(`/equipment/company/${companyId}`, { params }),
  addCompanyEquipment: (companyId, data) => api.post(`/equipment/company/${companyId}`, data),
  bulkAddCompanyEquipments: (companyId, data) => api.post(`/equipment/company/${companyId}/bulk`, data),
  // 보유장비 수정/삭제
  updateEquipment: (id, data) => api.patch(`/equipment/${id}`, data),
  deleteEquipment: (id) => api.delete(`/equipment/${id}`),
  // 분석
  getAnalytics: (params) => api.get('/equipment/analytics', { params }),
}

// General Equipment API (협력사 보유 장비현황 - 카테고리별)
export const generalEquipmentAPI = {
  // 카테고리
  getCategories: (params) => api.get('/general-equipment/categories', { params }),
  createCategory: (data) => api.post('/general-equipment/categories', data),
  updateCategory: (id, data) => api.patch(`/general-equipment/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/general-equipment/categories/${id}`),
  // 장비 마스터
  getMasters: (params) => api.get('/general-equipment/masters', { params }),
  createMaster: (data) => api.post('/general-equipment/masters', data),
  updateMaster: (id, data) => api.patch(`/general-equipment/masters/${id}`, data),
  deleteMaster: (id) => api.delete(`/general-equipment/masters/${id}`),
  // 내 업체 보유장비
  getMyEquipments: (params) => api.get('/general-equipment/my', { params }),
  addMyEquipment: (data) => api.post('/general-equipment/my', data),
  // 특정 업체 보유장비
  getCompanyEquipments: (companyId, params) => api.get(`/general-equipment/company/${companyId}`, { params }),
  addCompanyEquipment: (companyId, data) => api.post(`/general-equipment/company/${companyId}`, data),
  // 보유장비 수정/삭제
  updateEquipment: (id, data) => api.patch(`/general-equipment/${id}`, data),
  deleteEquipment: (id) => api.delete(`/general-equipment/${id}`),
  // 분석
  getAnalytics: (params) => api.get('/general-equipment/analytics', { params }),
}

// Company Profile API (협력사 프로필 관리)
export const companyProfileAPI = {
  // 비밀번호 변경
  changePassword: (data) => api.post('/company-profile/change-password', data),
  // GPS 좌표
  updateGPS: (data) => api.post('/company-profile/gps', data),
  // 담당자
  getContacts: () => api.get('/company-profile/contacts'),
  getCompanyContacts: (companyId) => api.get(`/company-profile/contacts/company/${companyId}`),
  addContact: (data) => api.post('/company-profile/contacts', data),
  updateContact: (id, data) => api.patch(`/company-profile/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/company-profile/contacts/${id}`),
  // 인증현황
  getCertifications: () => api.get('/company-profile/certifications'),
  getCompanyCertifications: (companyId) => api.get(`/company-profile/certifications/company/${companyId}`),
  addCertification: (data) => api.post('/company-profile/certifications', data),
  updateCertification: (id, data) => api.patch(`/company-profile/certifications/${id}`, data),
  deleteCertification: (id) => api.delete(`/company-profile/certifications/${id}`),
  // 인증서 파일 업로드 (PDF/이미지) — multipart 필드명 'file' → { url, filename }
  uploadCertificationFile: (formData) => api.post('/company-profile/certifications/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // 임시저장 / 승인요청
  saveDraft: (data) => api.post('/company-profile/draft', data),
  submitForApproval: (data) => api.post('/company-profile/submit', data),
  // 관리자 승인/반려
  approveProfile: (companyId) => api.post(`/company-profile/approve/${companyId}`),
  rejectProfile: (companyId, data) => api.post(`/company-profile/reject/${companyId}`, data),
  // 사출기 톤수별 집계
  getTonnageSummary: () => api.get('/company-profile/tonnage-summary'),
  getCompanyTonnageSummary: (companyId) => api.get(`/company-profile/tonnage-summary/${companyId}`),
  // 장비 승인 (관리자)
  getPendingEquipments: () => api.get('/company-profile/pending-equipments'),
  approveEquipment: (id, data) => api.post(`/company-profile/approve-equipment/${id}`, data),
  // 공장(다공장)
  getFactories: () => api.get('/company-profile/factories'),
  getCompanyFactories: (companyId) => api.get(`/company-profile/factories/company/${companyId}`),
  addFactory: (data) => api.post('/company-profile/factories', data),
  updateFactory: (id, data) => api.patch(`/company-profile/factories/${id}`, data),
  deleteFactory: (id) => api.delete(`/company-profile/factories/${id}`),
}

// Inspection Photo API (점검 사진 업로드/관리)
export const inspectionPhotoAPI = {
  // 사진 업로드 (카메라/갤러리)
  upload: (formData) => api.post('/inspection-photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // 금형별 사진 조회
  getByMold: (moldId, params) => api.get(`/inspection-photos/mold/${moldId}`, { params }),
  // 점검 항목별 사진 조회
  getByItem: (itemId, params) => api.get(`/inspection-photos/item/${itemId}`, { params }),
  // 점검 유형별 사진 조회
  getByType: (inspectionType, params) => api.get(`/inspection-photos/by-type/${inspectionType}`, { params }),
  // 수리요청 관련 사진 조회
  getByRepair: (repairRequestId) => api.get(`/inspection-photos/repair/${repairRequestId}`),
  // 엔티티별 사진 조회 (범용)
  getByEntity: (entityType, entityId) => api.get(`/inspection-photos/entity/${entityType}/${entityId}`),
  // 사진 상세 조회
  getById: (photoId) => api.get(`/inspection-photos/${photoId}`),
  // BYTEA 이미지 파일 URL
  getFileUrl: (photoId) => `/api/v1/inspection-photos/file/${photoId}`,
  // 사진 삭제 (soft delete)
  delete: (photoId) => api.delete(`/inspection-photos/${photoId}`),
  // 통계
  getStats: (params) => api.get('/inspection-photos/stats/summary', { params }),
}

// Mold Location API (금형 GPS 위치 추적/이력)
export const moldLocationAPI = {
  // 전체 금형 실시간 위치 조회
  getRealtime: (params) => api.get('/mold-locations/realtime', { params }),
  // 특정 금형 위치 이력
  getHistory: (moldId, params) => api.get(`/mold-locations/history/${moldId}`, { params }),
  // GPS 위치 수동 기록
  record: (data) => api.post('/mold-locations/record', data),
  // 기준 위치 설정 (관리자)
  setBase: (moldId, data) => api.put(`/mold-locations/base/${moldId}`, data),
  // 위치 이탈 금형 목록
  getDriftAlerts: () => api.get('/mold-locations/drift-alerts'),
  // GPS 위치 통계
  getStats: () => api.get('/mold-locations/stats'),
}

// User API (사용자 조회)
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  getDevelopers: () => api.get('/users', { params: { role: 'developer' } }),
}

// User Management API (사용자 관리)
export const userManagementAPI = {
  // 사내 사용자
  getInternalUsers: (params) => api.get('/user-management/internal', { params }),
  createInternalUser: (data) => api.post('/user-management/internal', data),
  updateInternalUser: (id, data) => api.put(`/user-management/internal/${id}`, data),
  resetInternalPassword: (id) => api.post(`/user-management/internal/${id}/reset-password`),
  
  // 협력사 사용자
  getPartnerUsers: (params) => api.get('/user-management/partner', { params }),
  createPartnerUser: (data) => api.post('/user-management/partner', data),
  updatePartnerUser: (id, data) => api.put(`/user-management/partner/${id}`, data),
  resetPartnerPassword: (id) => api.post(`/user-management/partner/${id}/reset-password`),
  
  // 승인 관리
  getPendingApprovals: () => api.get('/user-management/approvals/pending'),
  approveUser: (id) => api.post(`/user-management/approvals/${id}/approve`),
  rejectUser: (id, reason) => api.post(`/user-management/approvals/${id}/reject`, { reason }),
  
  // 공통
  getPermissionClasses: () => api.get('/user-management/permission-classes'),
  getDepartments: () => api.get('/user-management/departments'),
  deleteUser: (id) => api.delete(`/user-management/${id}`),
}

// Checklist Master API (체크리스트 마스터 관리)
export const checklistMasterAPI = {
  // 마스터 버전
  getVersions: (params) => api.get('/checklist-masters', { params }),
  getVersionById: (id) => api.get(`/checklist-masters/${id}`),
  createVersion: (data) => api.post('/checklist-masters', data),
  updateVersion: (id, data) => api.patch(`/checklist-masters/${id}`, data),
  deleteVersion: (id) => api.delete(`/checklist-masters/${id}`),
  submitForReview: (id) => api.post(`/checklist-masters/${id}/submit-review`),
  approve: (id) => api.post(`/checklist-masters/${id}/approve`),
  deploy: (id) => api.post(`/checklist-masters/${id}/deploy`),
  clone: (id) => api.post(`/checklist-masters/${id}/clone`),
  getDeployed: (params) => api.get('/checklist-masters/deployed', { params }),
  
  // 점검항목
  getItems: (params) => api.get('/checklist-masters/items', { params }),
  createItem: (data) => api.post('/checklist-masters/items', data),
  updateItem: (id, data) => api.patch(`/checklist-masters/items/${id}`, data),
  deleteItem: (id) => api.delete(`/checklist-masters/items/${id}`),
  
  // 주기 코드
  getCycles: () => api.get('/checklist-masters/cycles'),
}

// Standard Document Template API (표준문서 마스터 관리)
export const standardDocumentAPI = {
  getAll: (params) => api.get('/standard-document-templates', { params }),
  getById: (id) => api.get(`/standard-document-templates/${id}`),
  create: (data) => api.post('/standard-document-templates', data),
  update: (id, data) => api.patch(`/standard-document-templates/${id}`, data),
  approve: (id) => api.post(`/standard-document-templates/${id}/approve`),
  deploy: (id, data) => api.post(`/standard-document-templates/${id}/deploy`, data),
  duplicate: (id, data) => api.post(`/standard-document-templates/${id}/duplicate`, data),
  delete: (id) => api.delete(`/standard-document-templates/${id}`),
}

// Development Plan API (금형개발계획 관리)
export const developmentPlanAPI = {
  // 개발계획 목록 조회
  getAll: (params) => api.get('/development/plans', { params }),
  // 개발계획 상세 조회
  getById: (planId) => api.get(`/development/plans/${planId}`),
  // 금형별 개발계획 조회
  // Phase JZ-50: base-XXX 마커 ID 누출 차단 (NaverMoldLocationMap base marker id)
  getByMoldSpec: (moldSpecId) => {
    if (moldSpecId == null || (typeof moldSpecId === 'string' && /^(base-|current-)/.test(moldSpecId))) {
      return Promise.resolve({ data: { success: false, error: { message: 'Invalid mold_spec_id (marker id leak blocked)' } } });
    }
    return api.get(`/development/mold-spec/${moldSpecId}`);
  },
  // 개발계획 생성
  create: (data) => api.post('/development/plans', data),
  // 공정 단계 업데이트
  updateStep: (stepId, data) => api.patch(`/development/steps/${stepId}`, data),
  // 추진계획 항목 추가
  addStep: (planId, data) => api.post(`/development/plans/${planId}/steps`, data),
  // 추진계획 항목 삭제
  deleteStep: (planId, stepId) => api.delete(`/development/plans/${planId}/steps/${stepId}`),
  // 추진계획 항목 순서 변경
  reorderSteps: (planId, data) => api.put(`/development/plans/${planId}/steps/reorder`, data),
  // 기본 단계 마스터 목록 조회
  getDefaultSteps: () => api.get('/development/default-steps'),
  // 진행률 통계
  getProgressStatistics: () => api.get('/development/statistics/progress'),
}

// Inspection New API (통합 점검 시스템)
export const inspectionNewAPI = {
  // 점검 수행
  start: (data) => api.post('/inspections-new/start', data),
  getById: (id) => api.get(`/inspections-new/${id}`),
  saveDraft: (id, data) => api.patch(`/inspections-new/${id}/save-draft`, data),
  submit: (id, data) => api.post(`/inspections-new/${id}/submit`, data),
  
  // 점검 이력
  getHistory: (params) => api.get('/inspections-new', { params }),
  
  // 스케줄
  getDueSchedules: (params) => api.get('/inspections-new/schedules/due', { params }),
  getSchedules: (params) => api.get('/inspections-new/schedules', { params }),
  recalcSchedules: (params) => api.post('/inspections-new/schedules/recalc', null, { params }),
}

// Workflow API (승인 워크플로우)
export const workflowAPI = {
  // 금형개발 담당자 검색
  searchDevelopers: (params) => api.get('/workflow/developers/search', { params }),
  // 관리자(CAMS) 검색
  searchAdmins: (params) => api.get('/workflow/admins/search', { params }),
  // 제작처 검색
  searchMakers: (params) => api.get('/workflow/makers/search', { params }),
  // 생산처 검색
  searchPlants: (params) => api.get('/workflow/plants/search', { params }),
  // 수리요청 워크플로우
  createRepairRequest: (data) => api.post('/workflow/repair-requests', data),
  firstApprove: (id, data) => api.post(`/workflow/repair-requests/${id}/first-approve`, data),
  startRepair: (id, data) => api.post(`/workflow/repair-requests/${id}/start-repair`, data),
  completeRepair: (id, data) => api.post(`/workflow/repair-requests/${id}/complete-repair`, data),
  finalApprove: (id, data) => api.post(`/workflow/repair-requests/${id}/final-approve`, data),
  plantConfirm: (id, data) => api.post(`/workflow/repair-requests/${id}/plant-confirm`, data),
  reject: (id, data) => api.post(`/workflow/repair-requests/${id}/reject`, data),
  getRequests: (params) => api.get('/workflow/repair-requests', { params }),
}

// Notification API (알림) - alertAPI 통합
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getById: (id) => api.get(`/notifications/${id}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  send: (data) => api.post('/notifications', data),
  trigger: (data) => api.post('/notifications/trigger', data),
}

// 하위호환: alertAPI → notificationAPI alias
export const alertAPI = notificationAPI

// Code Options API (시스템 코드 옵션 마스터)
export const codeOptionsAPI = {
  getByCategory: (categories) => api.get('/code-options', { params: { category: Array.isArray(categories) ? categories.join(',') : categories } }),
  getCategories: () => api.get('/code-options'),
  create: (data) => api.post('/code-options', data),
  update: (id, data) => api.patch(`/code-options/${id}`, data),
  delete: (id) => api.delete(`/code-options/${id}`),
}

// 금형 투자비 관리 (본사 전용 — 엑셀형 편집 그리드)
//   list  : 그리드가 한 번에 받는 목록 + 드롭다운 옵션(filters) + 합계(totals)
//   bulk  : 변경된 행만 모아 한 트랜잭션 저장 (부분 성공 없음, 최대 500건)
export const moldInvestmentAPI = {
  list: (params) => api.get('/mold-investment', { params }),
  bulkUpdate: (items) => api.patch('/mold-investment/bulk', { items }),
}

// Phase G-2: Unified Task Center
export const taskCenterAPI = {
  getTasks: (params) => api.get('/tasks', { params }),
  getCounts: () => api.get('/tasks/counts'),
}

// Phase G-3: 동적 메뉴 권한
export const menuPermissionsAPI = {
  getMine: () => api.get('/menu-permissions/me'),
  list: (params) => api.get('/menu-permissions', { params }),
  saveAll: (permissions) => api.put('/menu-permissions', { permissions }),
}

// 협력사 초기화 + 39개 일괄 등록 (system_admin 전용)
export const seedCompaniesAPI = {
  preview: () => api.get('/admin/seed-companies/preview'),
  execute: (confirm) => api.post('/admin/seed-companies/execute', { confirm }),
}

// 금형 초기화 (system_admin 전용) — 보존할 mold_code 화이트리스트 외 일괄 삭제
export const resetMoldsAPI = {
  preview: (keepCodes) => api.get('/admin/reset-molds/preview', {
    params: keepCodes && keepCodes.length ? { keep_codes: keepCodes.join(',') } : {}
  }),
  execute: (confirm, keepCodes) => api.post('/admin/reset-molds/execute', {
    confirm,
    ...(keepCodes && keepCodes.length ? { keep_codes: keepCodes } : {}),
  }),
}

// Phase I-1: 표준 승인 엔진 (템플릿 기반)
export const approvalEngineAPI = {
  // 템플릿
  listTemplates: (params) => api.get('/approval-engine/templates', { params }),
  getDefaultTemplate: (targetType) => api.get(`/approval-engine/templates/${targetType}/default`),
  saveTemplate: (template, steps) => api.put('/approval-engine/templates', { template, steps }),
  // 승인 요청
  createRequest: (data) => api.post('/approval-engine/requests', data),
  approveStep: (id, data) => api.post(`/approval-engine/requests/${id}/approve`, data),
  rejectStep: (id, data) => api.post(`/approval-engine/requests/${id}/reject`, data),
  getRequest: (id) => api.get(`/approval-engine/requests/${id}`),
}

// Phase H-C: 긴급 우회
export const emergencyOverrideAPI = {
  create: (data) => api.post('/emergency-overrides', data),
  list: (params) => api.get('/emergency-overrides', { params }),
  reverse: (id, reason) => api.post(`/emergency-overrides/${id}/reverse`, { reason }),
}

export default api
